import React, { useEffect, useRef } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Box } from '@nerdwallet/currency';
import { COMPONENT_UPDATED } from '~/app/lib/redux/actions/action-types';
import UniversalText from '../universal-text/universal-text';
import {
  boolCheckboxSpec,
  repeatable,
  group,
  wrapper,
  stringSelectSpec,
  stringInputSpec,
} from '../../lib/editing-configs';
import UniversalContainer from '../universal-container/universal-container';
import { BulletListIcon } from './assets/bullet-list-icon';
import { universalContainerPropTypes } from '../universal-container';
import { useBuilder } from '../../lib/redux/selectors/builder-selector';
import './universal-bullet-list.scss';

const updateComponent = (component) => ({
  type: COMPONENT_UPDATED,
  component,
});

const defaultProps = {
  items: [
    {
      $unique_key: new Date().getTime(),
      header: {
        text: 'This is item 1',
      },
    },
    {
      $unique_key: new Date().getTime(),
      header: {
        text: 'This is item 2',
      },
    },
  ],
  disableDefaultProps: false,
};

const UniversalBulletList = (props) => {
  const {
    untouched,
    disableDefaultProps,
    componentId,
    listStyleType,
    listOrder,
    items,
    isCopy,
    imageUrl,
    imageWidth,
    imageHeight,
    container,
  } = props;
  const bulletListWithEmptyPropsRef = useRef();
  const dispatch = useDispatch();
  const { flatOutline, selected } = useBuilder();
  const selectedComponent = flatOutline?.[selected];

  const fromGrid = selectedComponent?.type === 'Grid';
  const shouldShowFromGridPreview = fromGrid && !items.length;

  const renderList = () => {
    const elementType = listOrder === 'ordered' ? 'ol' : 'ul';
    let className = '';

    if (elementType === 'ol') {
      className = 'ordered';
    } else if (listStyleType === 'dash') {
      className = 'mdash';
    } else if (listStyleType === 'imageUrl') {
      className = 'imageUrl';
    }

    const imageW = `${imageWidth}px`;
    const imageH = `${imageHeight}px`;

    const listImageStyle =
      listStyleType === 'imageUrl'
        ? {
            paddingLeft: 40,
            background: `url(${imageUrl}) no-repeat left center`,
            backgroundSize: `${imageW} ${imageH}`,
            margin: `10px 0`,
          }
        : {};

    const style = {
      listStyle: elementType === 'ul' ? listStyleType : undefined,
      paddingLeft: '1em',
    };

    const children = items.map((item) => (
      <li style={listImageStyle}>
        <UniversalText {...item?.header} />
      </li>
    ));
    return React.createElement(elementType, { style, className }, children);
  };

  useEffect(() => {
    if (untouched && !bulletListWithEmptyPropsRef.current) {
      bulletListWithEmptyPropsRef.current = selectedComponent;
    }
  }, [selectedComponent, untouched]);

  useEffect(() => {
    if (
      untouched &&
      selectedComponent?.id === componentId &&
      !disableDefaultProps &&
      !fromGrid &&
      !isCopy
    ) {
      dispatch(
        updateComponent({
          ...selectedComponent,
          props: { ...defaultProps, untouched: false },
        })
      );
    }
  }, [
    componentId,
    disableDefaultProps,
    selectedComponent,
    untouched,
    fromGrid,
    isCopy,
    dispatch,
  ]);

  useEffect(() => {
    if (disableDefaultProps) {
      dispatch(
        updateComponent({
          ...bulletListWithEmptyPropsRef.current,
          props: {
            ...bulletListWithEmptyPropsRef.current?.props,
            disableDefaultProps: true,
          },
        })
      );
    }
  }, [disableDefaultProps, dispatch, untouched]);

  if (shouldShowFromGridPreview) {
    return <BulletListIcon />;
  }

  return (
    <UniversalContainer {...container}>
      <Box>{renderList()}</Box>
    </UniversalContainer>
  );
};

UniversalBulletList.displayName = 'Bullet List';
UniversalBulletList.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Bullet list',
  fieldName = '',
  options = {
    disableGroup: true,
    disablePreview: false,
    includeContainer: false,
  }
) => {
  const getInputName = (name) => {
    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  const isInGrid = !!fieldName;

  const shouldOutputListStyleTypesInputName = (name) => {
    if (isInGrid) {
      return get(componentDocument?.props, `${fieldName}.${name}`, '');
    }
    return get(componentDocument?.props, name, '');
  };

  const listStyleType =
    shouldOutputListStyleTypesInputName('listOrder') === 'unordered'
      ? [
          stringSelectSpec(
            // 'listStyleType',
            getInputName('listStyleType'),
            'List style type',
            [
              { label: 'Disc', value: 'disc' },
              { label: 'Dash', value: 'dash' },
              { label: 'Image URL', value: 'imageUrl' },
            ],
            {
              help: 'Only applies to Desktop and Tablet',
            }
          ),
        ]
      : [];

  const listImageUrl =
    shouldOutputListStyleTypesInputName('listStyleType') === 'imageUrl' &&
    shouldOutputListStyleTypesInputName('listOrder') !== 'ordered'
      ? [
          stringInputSpec(getInputName('imageUrl'), 'Image URL'),
          stringInputSpec(getInputName('imageWidth'), 'Image width'),
          stringInputSpec(getInputName('imageHeight'), 'Image height'),
        ]
      : [];

  const editorPanelOptions = [
    group('List Styles', [
      stringSelectSpec(
        getInputName('listOrder'),
        'List order',
        [
          { label: 'Ordered', value: 'ordered' },
          { label: 'Unordered', value: 'unordered' },
        ],
        {
          help: 'Only applies to Desktop and Tablet',
        }
      ),
      ...listStyleType,
    ]),
    ...listImageUrl,
    repeatable(`${getInputName('items')}`, 'List items', 'Items', (item) => [
      ...UniversalText.getEditingConfig(
        componentDocument,
        'List text',
        [
          'fontWeight',
          'groupButton',
          'margin',
          'padding',
          'fontFamily',
          'textColor',
          'textSize',
          'textType',
        ],
        `${getInputName(`items.${item}.header`)}`,
        { text: { textType: 'title' }, disableGroup: true }
      ),
    ]),
  ];

  const disableDefaultPropsInput = isInGrid
    ? []
    : [boolCheckboxSpec(`disableDefaultProps`, 'Disable Default Properties?')];

  if (!options.disablePreview) {
    editorPanelOptions.unshift(
      ...disableDefaultPropsInput,
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'hide',
          'jumpLinkId',
        ],
        `${getInputName('container')}`
      )
    );
  }

  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

UniversalBulletList.defaultProps = {
  fullWidthMobile: false,
  items: [],
  titleSize: '2',
  size: '',
  listOrder: 'ordered',
  listStyleType: 'disc',
  imageWidth: '30',
  imageHeight: '30',
  imageUrl: 'https://picsum.photos/200/300',
  disableDefaultProps: false,
};

UniversalBulletList.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  strokeColorOptions: PropTypes.object,
  rollOverColorOptions: PropTypes.object,
  fullWidthMobile: PropTypes.bool,
  titleSize: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
    })
  ),
  listStyleType: PropTypes.string,
  listOrder: PropTypes.string,
  imageWidth: PropTypes.string,
  imageHeight: PropTypes.string,
  imageUrl: PropTypes.string,
  size: PropTypes.string,
  outputSchema: PropTypes.bool,
  disableDefaultProps: PropTypes.bool,
  componentId: PropTypes.string,
  untouched: PropTypes.bool,
  updateComponentProps: PropTypes.func,
  container: universalContainerPropTypes,
  isCopy: PropTypes.bool,
};

export default UniversalBulletList;
